import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import usePrevious from 'common/usePrevious';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import { getInsuranceOptionID } from 'helper/insurance';
import { allDeliveriesMapSelector } from 'selectors/new-booking-selectors';
import { isBulkUploadDeliveryID } from 'constants/anytime-booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';

// TODO: check whether all fields are ready, if yes, proceed
const deliveryCanFetchQuote = deliveryDetail =>
  _get(deliveryDetail, 'pickUp.poi.id') &&
  _get(deliveryDetail, 'dropOffs[0].poi.id');

const dropOffsGetter = deliveryDetail =>
  _get(deliveryDetail, ['detail', 'dropOffs'], []).map(dropOff => [
    _get(dropOff, ['poi', 'id']),
    _get(dropOff, ['cod']),
    getInsuranceOptionID(_get(dropOff, 'insuranceOption')),
  ]);

// fields that will trigger getQuote
const signatureFieldGetters = [
  ['routeOptimization', 'enabled'],
  ['routeOptimization', 'preventAutoApplyRO'],
  ['detail', 'isRoundTrip'],
  ['detail', 'itemWeight'],
  ['detail', 'parcelTotalSize'],
  ['detail', 'itemType'],
  ['detail', 'promoCode'],
  ['detail', 'paymentTypeID'],
  ['detail', 'pickUp', 'poi', 'id'],
  dropOffsGetter,
  ['detail', 'configuration', 'serviceType'],
  ['detail', 'configuration', 'specialServiceType'],
  ['detail', 'configuration', 'vehicleType'],
  ['detail', 'configuration', 'additionalServices'],
  ['detail', 'configuration', 'scheduledPickupSlot'],
  ['detail', 'configuration', 'offerConfig', 'offerID'],
  ['detail', 'configuration', 'offerConfig', 'id'],
];

const getDeliverySignature = delivery =>
  signatureFieldGetters.map(
    getter =>
      typeof getter === 'function' ? getter(delivery) : _get(delivery, getter),
  );

const DeliveryConfigListener = ({ dispatch, allDeliveriesMap }) => {
  const [deliverySignatures, setDeliverySignatures] = useState({});
  const previousDeliverySignatures = usePrevious(deliverySignatures);

  const debounceChangeSignature = useCallback(
    _debounce(allDeliveriesMap => {
      const signature = Object.entries(allDeliveriesMap).reduce(
        (prev, [deliveryID, delivery]) => ({
          ...prev,
          [deliveryID]: {
            signature: getDeliverySignature(delivery),
            canFetchQuote: deliveryCanFetchQuote(_get(delivery, ['detail'])),
          },
        }),
        {},
      );
      setDeliverySignatures(signature);
    }, 200),
    [],
  );

  useEffect(
    () => () => {
      debounceChangeSignature.cancel();
    },
    [debounceChangeSignature],
  );

  useEffect(
    () => {
      debounceChangeSignature(allDeliveriesMap);
    },
    [allDeliveriesMap],
  );

  useEffect(
    () => {
      const bulkUploadDeliveryIDs = [];
      Object.entries(deliverySignatures)
        .filter(([_, deliverySignature]) =>
          _get(deliverySignature, 'canFetchQuote', false),
        )
        .map(([deliveryID, deliverySignature]) => {
          const previousDeliverySignature =
            previousDeliverySignatures[deliveryID];
          if (
            !_isEqual(
              _get(deliverySignature, 'signature'),
              _get(previousDeliverySignature, 'signature'),
            )
          ) {
            if (isBulkUploadDeliveryID(deliveryID)) {
              bulkUploadDeliveryIDs.push(deliveryID);
              return;
            }
            dispatch(NewBookingDuck.creators.fetchDeliveryQuote(deliveryID));
          }
        });
      if (bulkUploadDeliveryIDs.length > 0) {
        dispatch(
          NewBookingDuck.creators.batchFetchDeliveryQuotes(
            bulkUploadDeliveryIDs,
          ),
        );
      }
    },
    [dispatch, deliverySignatures, previousDeliverySignatures],
  );

  return <div />;
};

DeliveryConfigListener.propTypes = {
  dispatch: PropTypes.func.isRequired,
  allDeliveriesMap: PropTypes.shape({}).isRequired,
};

export default connect(state => ({
  allDeliveriesMap: allDeliveriesMapSelector(state),
}))(DeliveryConfigListener);
